"use strict";
// The Downloads screen: every product the account bought, product by
// product, with its files once delivered.
//
// Flix sells digital goods and delivers each product by putting its Drive
// link into that product's fulfilment in Shopify (Flix Management does it
// per product; staff did it by hand before). GET /me/order-files reads
// exactly that, live: each link belongs to the products its fulfilment
// lists, so an order of several products shows each with its own files.
// Orders placed since the screen went live also show what is still on its
// way; older ones only what was already delivered.
//
// Nothing here is invented. There is no file size, no format, no expiry
// and no download count: Shopify does not know any of those about a link
// in a text field. Product names and order numbers are shown in full.
(function () {
    function t(hubEl, key, fallback) {
        return hubEl.dataset[key] || fallback;
    }
    var esc = function (value) {
        return window.FlixHubFormat.escapeHtml(value);
    };
    var DOWNLOAD_ICON = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>';
    var OPEN_ICON = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3h6v6"/><path d="M10 14 21 3"/><path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5"/></svg>';
    var STATUS_STYLE = {
        delivered: "background:color-mix(in srgb, var(--success) 14%, transparent);border:1px solid color-mix(in srgb, var(--success) 40%, transparent);color:var(--success)",
        pending: "background:rgba(245,166,35,.14);border:1px solid rgba(245,166,35,.4);color:var(--warning)",
    };
    /**
     * The link came out of a free-text field a human typed into, so the
     * backend already drops anything that is not http(s) before it is ever
     * sent here. This repeats the check on the way into an href because
     * that is the one place where being wrong would be a scripted URL
     * running in the shopper's session.
     */
    function isOpenableUrl(url) {
        return /^https?:\/\//i.test(url);
    }
    /** The section label the Hub uses everywhere: mono 10px, 2px tracking. */
    function sectionLabelHtml(label) {
        return ('<div style="font-family:var(--mono);font-size:10px;letter-spacing:2px;color:var(--accent);margin:0 0 10px;text-transform:uppercase">' +
            esc(label) +
            "</div>");
    }
    function thumbHtml(imageUrl) {
        if (imageUrl) {
            return ('<img src="' +
                esc(imageUrl) +
                '" alt="" style="width:48px;height:48px;border-radius:10px;object-fit:cover;flex:none">');
        }
        return ('<span aria-hidden="true" style="width:48px;height:48px;border-radius:10px;background:var(--surface2);' +
            'display:flex;align-items:center;justify-content:center;flex:none;color:var(--faint)">' +
            DOWNLOAD_ICON +
            "</span>");
    }
    /**
     * Each file is an anchor, not a button: it hands the shopper a real file
     * on a host we do not control, so it must behave like a link — a new tab
     * keeps the Hub, and the session behind it, exactly where it was.
     */
    function fileLinksHtml(hubEl, urls) {
        var label = t(hubEl, "i18nDownloadsDownload", "Download files");
        return ('<div style="display:flex;flex-wrap:wrap;gap:8px;margin-top:12px">' +
            urls
                .map(function (url, i) {
                return ('<a data-flix-download-row href="' +
                    esc(url) +
                    '" target="_blank" rel="noopener noreferrer" ' +
                    'style="display:inline-flex;align-items:center;gap:7px;padding:10px 16px;border-radius:var(--radius-button);border:none;' +
                    "background:linear-gradient(135deg,var(--accent),var(--accent2));color:#160d05;font-family:var(--display);font-weight:800;" +
                    'font-size:13px;text-decoration:none" style-hover="transform:translateY(-2px)">' +
                    DOWNLOAD_ICON.replace(/width="18" height="18"/, 'width="16" height="16"') +
                    esc(urls.length > 1 ? label + " " + (i + 1) : label) +
                    "</a>");
            })
                .join("") +
            "</div>");
    }
    function itemHtml(hubEl, item, locale) {
        // A download button only ever on a product that was delivered.
        var urls = item.status === "delivered" ? item.urls.filter(isOpenableUrl) : [];
        var statusLabel = item.status === "delivered"
            ? t(hubEl, "i18nDownloadsStatusDelivered", "Delivered")
            : t(hubEl, "i18nDownloadsStatusPending", "In progress");
        var date = window.FlixHubFormat.date(item.deliveredAt || item.orderedAt, locale);
        var below;
        if (urls.length > 0) {
            below = fileLinksHtml(hubEl, urls);
        }
        else if (item.status === "pending") {
            below =
                '<p style="font-size:13px;color:var(--dim);margin:10px 0 0;line-height:1.6">' +
                    esc(t(hubEl, "i18nDownloadsPendingMessage", "Your files will appear here once delivered.")) +
                    "</p>";
        }
        else {
            below =
                '<a href="' +
                    esc(t(hubEl, "supportUrl", "/pages/support")) +
                    '" style="display:inline-flex;align-items:center;gap:6px;margin-top:10px;font-size:13px;color:var(--accent);text-decoration:none">' +
                    esc(t(hubEl, "i18nDownloadsNoFile", "Contact support to get your files.")) +
                    OPEN_ICON +
                    "</a>";
        }
        // Names wrap instead of being cut: a product name or an order number is
        // never shortened. The order number is an LTR token inside RTL text.
        return ('<div data-flix-download-item data-status="' +
            item.status +
            '" style="display:flex;align-items:flex-start;gap:14px;padding:14px 16px;border-radius:var(--radius-md);' +
            'background:var(--surface);border:1px solid var(--border)">' +
            thumbHtml(item.imageUrl) +
            '<div style="min-width:0;flex:1">' +
            '<div style="display:flex;flex-wrap:wrap;align-items:center;gap:8px">' +
            '<span style="font-size:14px;font-weight:700;color:var(--text);line-height:1.5;overflow-wrap:anywhere">' +
            esc(item.productName) +
            "</span>" +
            '<span style="flex:none;padding:3px 10px;border-radius:999px;font-size:11.5px;font-weight:700;' +
            STATUS_STYLE[item.status] +
            '">' +
            esc(statusLabel) +
            "</span>" +
            "</div>" +
            '<div style="font-size:12px;color:var(--dim);margin-top:4px;line-height:1.6;overflow-wrap:anywhere">' +
            esc(t(hubEl, "i18nDownloadsOrder", "Order")) +
            ' <bdi dir="ltr">' +
            esc(item.orderName) +
            "</bdi> · " +
            esc(date) +
            "</div>" +
            below +
            "</div>" +
            "</div>");
    }
    async function render(hubEl, contentEl) {
        var locale = document.documentElement.lang || "en";
        contentEl.innerHTML = window.FlixHubRender.loadingState(t(hubEl, "i18nLoading", "Loading…"));
        var page;
        try {
            page = await window.FlixAuthClient.listOrderFiles();
        }
        catch (err) {
            // The backend answers 503 rather than an empty list when it cannot
            // reach Shopify, precisely so this screen never tells someone their
            // files are gone when they are not.
            contentEl.innerHTML = window.FlixHubRender.errorState({
                title: t(hubEl, "i18nErrorTitle", "Something went wrong"),
                message: t(hubEl, "i18nDownloadsErrorMessage", "We couldn't load your files just now. Please try again."),
                retryLabel: t(hubEl, "i18nRetry", "Retry"),
            });
            window.flixCompileHoverStyles(contentEl);
            return;
        }
        if (page.items.length === 0) {
            contentEl.innerHTML = window.FlixHubRender.emptyState({
                title: t(hubEl, "i18nDownloadsEmptyTitle", "No files yet"),
                message: t(hubEl, "i18nDownloadsEmptyMessage", "Files from your purchases appear here as soon as they are delivered."),
                iconSvg: DOWNLOAD_ICON,
                action: { label: t(hubEl, "i18nDownloadsEmptyCta", "Browse the store"), href: "/" },
            });
            window.flixCompileHoverStyles(contentEl);
            return;
        }
        // Said only when the backend actually stopped short of the end of the
        // order history, so the list is never presented as the whole truth
        // when it isn't — and never carries the caveat when it is.
        var truncatedNote = page.truncated
            ? '<p style="font-size:12px;color:var(--faint);margin:12px 2px 0;line-height:1.6">' +
                esc(t(hubEl, "i18nDownloadsTruncated", "Showing your most recent files. Older purchases may not be listed here.")) +
                "</p>"
            : "";
        contentEl.innerHTML =
            sectionLabelHtml(t(hubEl, "i18nDownloadsLabel", "Your files")) +
                '<div data-flix-downloads-list style="display:flex;flex-direction:column;gap:10px">' +
                page.items
                    .map(function (item) {
                    return itemHtml(hubEl, item, locale);
                })
                    .join("") +
                "</div>" +
                truncatedNote;
        window.flixCompileHoverStyles(contentEl);
    }
    window.FlixHubDownloads = { render: render };
})();
